import type { CSSProperties, ReactNode } from "react";

// Shared SVG icon set — we use our own marks instead of emoji glyphs so symbols
// render consistently across platforms and inherit text colour (currentColor).
// Stroke icons follow the Feather style already used in the navbar.

export interface IconProps {
  size?: number | string;
  className?: string;
  style?: CSSProperties;
  title?: string;
}

const Svg = ({
  size = "1em",
  className,
  style,
  title,
  viewBox = "0 0 24 24",
  fill = "none",
  stroke = true,
  children,
}: IconProps & {
  viewBox?: string;
  fill?: string;
  stroke?: boolean;
  children: ReactNode;
}) => (
  <svg
    width={size}
    height={size}
    viewBox={viewBox}
    fill={fill}
    stroke={stroke ? "currentColor" : "none"}
    strokeWidth={stroke ? 2 : undefined}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    style={style}
    role={title ? "img" : undefined}
    aria-hidden={title ? undefined : true}
    aria-label={title}
    focusable="false"
  >
    {title ? <title>{title}</title> : null}
    {children}
  </svg>
);

/* ── Trend / polarity (filled, coloured by CSS) ── */
export const TrendUp = (p: IconProps) => (
  <Svg {...p} fill="currentColor" stroke={false}>
    <path d="M12 5l7 13H5z" />
  </Svg>
);
export const TrendDown = (p: IconProps) => (
  <Svg {...p} fill="currentColor" stroke={false}>
    <path d="M12 19L5 6h14z" />
  </Svg>
);
export const TrendFlat = (p: IconProps) => (
  <Svg {...p} fill="currentColor" stroke={false}>
    <rect x="5" y="10" width="14" height="4" rx="1.5" />
  </Svg>
);

/* ── Generic UI ── */
export const MapPin = (p: IconProps) => (
  <Svg {...p}>
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </Svg>
);
export const Calendar = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </Svg>
);
export const Clock = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </Svg>
);
export const Search = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.3-4.3" />
  </Svg>
);
export const Close = (p: IconProps) => (
  <Svg {...p}>
    <path d="M18 6L6 18M6 6l12 12" />
  </Svg>
);
export const ChevronRight = (p: IconProps) => (
  <Svg {...p}>
    <path d="M9 6l6 6-6 6" />
  </Svg>
);
export const ChevronDown = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6 9l6 6 6-6" />
  </Svg>
);
export const ArrowLeft = (p: IconProps) => (
  <Svg {...p}>
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </Svg>
);
export const ArrowRight = (p: IconProps) => (
  <Svg {...p}>
    <path d="M5 12h14M12 5l7 7-7 7" />
  </Svg>
);
export const Cpu = (p: IconProps) => (
  <Svg {...p}>
    <rect x="4" y="4" width="16" height="16" rx="2" />
    <rect x="9" y="9" width="6" height="6" />
    <path d="M9 1v3M15 1v3M9 20v3M15 20v3M20 9h3M20 14h3M1 9h3M1 14h3" />
  </Svg>
);
export const Cloud = (p: IconProps) => (
  <Svg {...p}>
    <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
  </Svg>
);
export const BarChart = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 20V10M18 20V4M6 20v-4" />
  </Svg>
);
export const Lock = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </Svg>
);
export const User = (p: IconProps) => (
  <Svg {...p}>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </Svg>
);
export const Mail = (p: IconProps) => (
  <Svg {...p}>
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="M22 6l-10 7L2 6" />
  </Svg>
);
export const Star = (p: IconProps) => (
  <Svg {...p} fill="currentColor" stroke={false}>
    <path d="M12 2l2.9 6.26 6.86.6-5.18 4.52 1.55 6.71L12 16.9 5.87 20.6l1.55-6.71-5.18-4.53 6.86-.59z" />
  </Svg>
);

/* ── Flags (filled, fixed palette) ── */
export const FlagSE = ({ size = "1em", className, style, title }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 10"
    className={className}
    style={style}
    role="img"
    aria-label={title || "Svenska"}
    focusable="false"
  >
    <rect width="16" height="10" fill="#006aa7" />
    <rect x="5" width="2" height="10" fill="#fecc00" />
    <rect y="4" width="16" height="2" fill="#fecc00" />
  </svg>
);
export const FlagNO = ({ size = "1em", className, style, title }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 10"
    className={className}
    style={style}
    role="img"
    aria-label={title || "Norge"}
    focusable="false"
  >
    <rect width="16" height="10" fill="#ba0c2f" />
    <rect x="4" width="4" height="10" fill="#fff" />
    <rect y="3" width="16" height="4" fill="#fff" />
    <rect x="5" width="2" height="10" fill="#00205b" />
    <rect y="4" width="16" height="2" fill="#00205b" />
  </svg>
);
export const FlagFI = ({ size = "1em", className, style, title }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 10"
    className={className}
    style={style}
    role="img"
    aria-label={title || "Suomi"}
    focusable="false"
  >
    <rect width="16" height="10" fill="#fff" />
    <rect x="4.5" width="3" height="10" fill="#002f6c" />
    <rect y="3.5" width="16" height="3" fill="#002f6c" />
  </svg>
);
export const FlagDK = ({ size = "1em", className, style, title }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 10"
    className={className}
    style={style}
    role="img"
    aria-label={title || "Danmark"}
    focusable="false"
  >
    <rect width="16" height="10" fill="#c8102e" />
    <rect x="5" width="2" height="10" fill="#fff" />
    <rect y="4" width="16" height="2" fill="#fff" />
  </svg>
);
export const FlagGB = ({ size = "1em", className, style, title }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 10"
    className={className}
    style={style}
    role="img"
    aria-label={title || "English"}
    focusable="false"
  >
    <rect width="16" height="10" fill="#012169" />
    <path d="M0 0l16 10M16 0L0 10" stroke="#fff" strokeWidth="2" />
    <path d="M0 0l16 10M16 0L0 10" stroke="#c8102e" strokeWidth="1" />
    <rect x="6.5" width="3" height="10" fill="#fff" />
    <rect y="3.5" width="16" height="3" fill="#fff" />
    <rect x="7" width="2" height="10" fill="#c8102e" />
    <rect y="4" width="16" height="2" fill="#c8102e" />
  </svg>
);
